/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : rule_center.js
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Typography,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  CustomButton,
  CustomAddDialog,
  CustomTextFieldN,
  SelectTextFieldN,
  CustomDataGridTableN,
  CustomPagination,
  MultiSelectTextFieldNVs,
} from "../../components";
import AddIcon from "@mui/icons-material/Add";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import appTheme from "../../assets/AppTheme/appTheme";
import { HandleApiActions, legalRequirements } from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import ruleEngineActions from "../../redux/actions/ruleEngineAction";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import AddDialog from "../../components/customComponentsV2/projectConfig/AddDialog";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomCheckboxMultiSelect from "../../components/customComponentsV2/common/multiselect/CustomCheckboxMultiSelect";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";
import CommonInputSwitch from "../../components/customComponentsV2/common/InputSwitch/CommonInputSwitch";
import { fontSizeDetails } from "../../config/appTheme";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { useNavigate } from "react-router-dom";

// MultiSelectUsers import removed because it's not used in this file

// --- Recursive Question Node UI ---
const QuestionNode = ({
  question,
  onAddBranch,
  onUpdateQuestion,
  onSaveQuestion,
  onRemoveUnsavedQuestion,
  ruleSetId,
  forceRender = 0,
  autoOpenId,
  clearAutoOpenId,
  isRoot = false,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [questionText, setQuestionText] = React.useState(question.text);
  const [questionType, setQuestionType] = React.useState(
    isRoot ? "question" : question.type || "question",
  );
  const [requiresHomologation, setRequiresHomologation] = React.useState(
    question.requires_homologation || false,
  );

  /* ---------------- STATE SYNC ---------------- */
  React.useEffect(() => {
    if (!isEditing) {
      setQuestionText(question.text);
      setQuestionType(isRoot ? "question" : question.type || "question");
      setRequiresHomologation(question.requires_homologation || false);
    }
  }, [
    question.id,
    question.text,
    question.type,
    question.requires_homologation,
    isEditing,
    isRoot,
  ]);

  /* ---------------- SAVE ---------------- */
  const handleSave = () => {
    onUpdateQuestion(
      question.id,
      questionText,
      questionType,
      requiresHomologation,
    );
    onSaveQuestion(
      question.id,
      questionText,
      questionType,
      ruleSetId,
      requiresHomologation,
    );
    setIsEditing(false);
  };

  /* ---------------- AUTO OPEN ---------------- */
  React.useEffect(() => {
    if (autoOpenId && question.id === autoOpenId && !isEditing) {
      setIsEditing(true);
      clearAutoOpenId?.();
    }
  }, [autoOpenId, question.id, isEditing, clearAutoOpenId]);

  /* ---------------- CANCEL ---------------- */
  const handleCancel = () => {
    if (question.isNew && !question.server_id) {
      onRemoveUnsavedQuestion?.(question.id);
    } else {
      setQuestionText(question.text);
      setQuestionType(question.type || "question");
      setRequiresHomologation(question.requires_homologation || false);
    }
    setIsEditing(false);
  };

  /* ---------------- SAVED CHECK ---------------- */
  const isQuestionSaved = !!question.server_id;

  /* ============================================================
     ======================= UI START ===========================
     ============================================================ */

  return (
    <Box
      sx={{
        border: "1px solid #ddd",
        width: "100%",
        borderRadius: 2,
        p: 2,
        mt: 2,
        backgroundColor: isRoot ? "#fff" : "#fff",
      }}
    >
      {/* ================= VIEW MODE ================= */}
      {!isEditing && (
        <>
          <Box display="flex" justifyContent="space-between">
            <Box>
              <Typography fontWeight="bold">
                {question.text || "Click Edit to add question text"}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Type:{" "}
                {isRoot
                  ? "Root Question"
                  : question.type === "category"
                    ? "Category / End Session"
                    : "Question"}
              </Typography>

              {isQuestionSaved ? (
                <Typography
                  variant="caption"
                  color="success.main"
                  display="block"
                >
                  ✓ Saved
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  color="warning.main"
                  display="block"
                >
                  ⚠ Save required to add branches
                </Typography>
              )}
            </Box>

            <CustomButtonV2
              size="small"
              variant="outlined"
              btnName="Edit"
              handleOnClick={() => setIsEditing(true)}
            />
          </Box>

          {/* ---------------- ACTION BUTTONS ---------------- */}
          {questionType === "question" && (
            <Box display="flex" gap={2} mt={2}>
              <CustomButtonV2
                size="small"
                variant="outlined"
                btnName="Add Yes Branch"
                disabled={!isQuestionSaved}
                handleOnClick={() => onAddBranch(question.id, "YES")}
              />
              <CustomButtonV2
                size="small"
                variant="outlined"
                btnName="Add No Branch"
                disabled={!isQuestionSaved}
                handleOnClick={() => onAddBranch(question.id, "NO")}
              />
            </Box>
          )}

          {questionType === "category" && (
            <Box mt={2} p={1} bgcolor="#f5f5f5" borderRadius={1}>
              <Typography variant="body2" color="success.main">
                ✓ End Session Category
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* ================= EDIT MODE ================= */}
      {isEditing && (
        <>
          <CustomFormTextFieldV2
            size="small"
            label="Question / Category Text"
            required
            value={questionText}
            setValue={setQuestionText}
            isMultiline
            txtFldStyle={{ width: "100%", mb: 2 }}
          />

          {!isRoot && (
            <CustomSelectV2
              size="small"
              label="Type"
              selectedValue={questionType}
              handleOnChange={(e) => setQuestionType(e.target.value)}
              optionsList={[
                { value: "question", label: "Question" },
                { value: "category", label: "Category / End Session" },
              ]}
              txtFldStyle={{ width: "100%", mb: 2 }}
            />
          )}

          {questionType === "category" && (
            <CommonInputSwitch
              checked={requiresHomologation}
              onChange={(checked) => setRequiresHomologation(checked)}
              label="Homologation Required"
            />
          )}

          <Box display="flex" gap={1} mt={2}>
            <CustomButtonV2
              size="small"
              variant="contained"
              btnName="Save"
              disabled={!questionText.trim()}
              handleOnClick={handleSave}
            />
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="Cancel"
              handleOnClick={handleCancel}
            />
          </Box>
        </>
      )}

      {/* =================================================
         ============ CHILD QUESTIONS (VERTICAL) =========
         ================================================= */}
      {questionType === "question" && (question.yes || question.no) && (
        <Box
          mt={2}
          pl={2}
          borderLeft="3px solid #e0e0e0"
          width="100%"
          boxSizing="border-box"
        >
          {question.yes && (
            <Box mb={2}>
              <Typography fontWeight="bold" color="green" mb={1}>
                YES
              </Typography>
              <QuestionNode
                question={question.yes}
                onAddBranch={onAddBranch}
                onUpdateQuestion={onUpdateQuestion}
                onSaveQuestion={onSaveQuestion}
                onRemoveUnsavedQuestion={onRemoveUnsavedQuestion}
                ruleSetId={ruleSetId}
                forceRender={forceRender}
                autoOpenId={autoOpenId}
                clearAutoOpenId={clearAutoOpenId}
                isRoot={false}
              />
            </Box>
          )}

          {question.no && (
            <Box>
              <Typography
                fontWeight="bold"
                color="red"
                mb={1}
                sx={styles.fontSub1}
              >
                NO
              </Typography>
              <QuestionNode
                question={question.no}
                onAddBranch={onAddBranch}
                onUpdateQuestion={onUpdateQuestion}
                onSaveQuestion={onSaveQuestion}
                onRemoveUnsavedQuestion={onRemoveUnsavedQuestion}
                ruleSetId={ruleSetId}
                forceRender={forceRender}
                autoOpenId={autoOpenId}
                clearAutoOpenId={clearAutoOpenId}
                isRoot={false}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

function RuleCenter() {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const timeZoneDetails = useSelector((state) => state.profileReducer);

  // Force re-render trigger
  const [forceRender, setForceRender] = useState(0);
  // Track most recently added branch UI id for single-time auto-open
  const [recentlyAddedQuestionId, setRecentlyAddedQuestionId] = useState(null);

  // State for rule sets
  const [displayRuleSets, setDisplayRuleSets] = useState([]);

  // Dialog states
  const [isAddRuleSetDialogOpen, setIsAddRuleSetDialogOpen] = useState(false);
  const [isAddQuestionDialogOpen, setIsAddQuestionDialogOpen] = useState(false);

  // Form states
  const [ruleSetName, setRuleSetName] = useState("");
  const [ruleSetDescription, setRuleSetDescription] = useState("");

  // Selected rule for adding questions
  const [selectedRuleSet, setSelectedRuleSet] = useState(null);
  const [questionTree, setQuestionTree] = useState(null);

  /* Legal Standards */
  const [legalStandardsList, setLegalStandardsList] = useState([]);
  const [legalStandardsOptionsList, setLegalStandardsOptionsList] = useState(
    [],
  );
  // allow multiple legal standards
  const [selectedLegalStandardIds, setSelectedLegalStandardIds] = useState([]);
  console.log(selectedLegalStandardIds, "selectedLegalStandardIds");

  //Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Get Rule Sets List Function
  const getRuleSetsList = useCallback(async () => {
    const resp = await dispatch(
      HandleApiActions({
        ...ruleEngineActions.get_rule_engine,
        params: {
          page_number: page,
          page_size: rowsPerPage,
        },
        show_toast: false,
      }),
    );

    console.log("Rule sets response:", resp);

    if (resp.error) {
      setDisplayRuleSets([]);
    } else {
      // Format the data for the table with consistent S.No
      const formattedRuleSets =
        resp?.data?.map((ruleSet, index) => ({
          id: (page - 1) * rowsPerPage + index + 1,
          ruleSetName: ruleSet?.name,
          description: ruleSet?.description,
          legalStandard: ruleSet?.standard_number,
          rule_set_id: ruleSet?.rule_set_id,
          created_by: ruleSet?.created_by_name,
          created_at: convertUtcToTimeZone(
            ruleSet?.created_at,
            timeZoneDetails?.profile?.profile?.region,
          ),
        })) || [];

      setDisplayRuleSets(formattedRuleSets);
      setTotalCount(resp?.total_count);
      console.log(resp, "resp..jj.");
      // Handle pagination hasMore
      if (formattedRuleSets?.length < rowsPerPage) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    }
  }, [dispatch, page, rowsPerPage]);

  // Table columns configuration
  const columns = [
    {
      field: "id",
      headerName: "S.No",
      flex: 0.4,
      minWidth: 60,
      maxWidth: 80,
      filterable: false,
      sortable: false,
    },
    {
      field: "ruleSetName",
      headerName: "Rule Set Name",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "legalStandard",
      headerName: "Legal Standard",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const value = params.value || [];

        return (
          <CustomSelectV2
            label=""
            required={false}
            optionsList={
              Array.isArray(value)
                ? value.map((opt) => ({
                    label: opt,
                    value: opt,
                  }))
                : []
            }
            selectedValue={
              Array.isArray(value) && value.length > 0 ? value[0] : ""
            }
            sx={{
              width: "90%",
              fontSize: "0.875rem", // match DataGrid font
            }}
          />
        );
      },
    },

    {
      field: "created_by",
      headerName: "Created By",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box>
          {/* <IconButton
            size="small"
            onClick={() => handleEditRuleSet(params.row)}
            sx={{ color: appTheme?.palette?.primary?.main }}
          >
            <EditIcon />
          </IconButton> */}
          <IconButton
            size="small"
            onClick={() => handleOpenQuestions(params.row)}
            sx={{ color: appTheme?.palette?.secondary?.main }}
          >
            <BorderColorIcon sx={{ color: "#000", fontSize: 16 }} />{" "}
          </IconButton>
        </Box>
      ),
    },
  ];

  // UseEffects - Use getRuleSetsList for consistent data fetching
  useEffect(() => {
    getRuleSetsList();
  }, [getRuleSetsList]);

  // Handle page change
  const handlePageChange = (value) => {
    console.log("value", value);
    if (page !== value) {
      setPage(value);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1); // Reset to the first page when page size changes
  };

  /* useEffects */
  useEffect(() => {
    // Get the Legal Standards List
    const getLegalStandardsList = async () => {
      let apiDetails = legalRequirements.get_legal_standard_details_new;

      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            page_number: 1,
            page_size: 250,
          },
          show_toast: false,
        }),
      );

      console.log("resdfsafvjishsohkfsfhvsvgsf", resp);

      if (resp.error) {
        setLegalStandardsList([]);
      } else {
        setLegalStandardsList(resp.data);
      }
    };

    getLegalStandardsList();
  }, [dispatch]);

  console.log("legalStandardsList", legalStandardsList);
  console.log("displayRuleSets", displayRuleSets);

  useEffect(() => {
    /* Get Legal Standards Options List */
    // Map API items into the { id, name } shape expected by
    // MultiSelectTextFieldNVs. Use numeric ls_id for id so
    // parseInt()/Number comparisons inside the component work.
    let filteredLegalStandardsOptionsList = legalStandardsList
      ?.map((el) => ({
        id: el?.ls_id
          ? isNaN(Number(el.ls_id))
            ? el.ls_id
            : Number(el.ls_id)
          : el?.ls_id,
        name:
          el?.standard_number && el?.name
            ? `${el.standard_number}`
            : el?.standard_number || el?.name || `LS ${el?.ls_id}`,
        version: el?.version,
      }))
      ?.sort((a, b) =>
        (a.name || "").toString().localeCompare((b.name || "").toString()),
      );

    setLegalStandardsOptionsList(filteredLegalStandardsOptionsList || []);
  }, [legalStandardsList]);

  console.log("legalStandardsOptionsList", legalStandardsOptionsList);

  //Add rule set
  const addRuleSet = async () => {
    const selectedIds = selectedLegalStandardIds.map((item) => item.value);
    let selectedOptions = legalStandardsOptionsList.filter((option) => {
      return selectedIds.includes(option.id);
    });
    console.log(selectedOptions, "selectedOptions");

    const resp = await dispatch(
      HandleApiActions({
        ...ruleEngineActions.add_rule_engine,
        params: {
          name: ruleSetName,
          description: ruleSetDescription,
          standard: selectedOptions.map((option) => option.name),
        },
        show_toast: true,
      }),
    );

    if (!resp.error) {
      const ruleSetId = resp.data?.rule_set_id;

      toggleAddRuleSetDialog(false)();

      navigate(`/dashboard/rule-center/${ruleSetId}/workflow`, {
        state: {
          ruleSetName: ruleSetName,
        },
      });
    } else {
      console.log("Failed to add rule set");
    }
  };

  // Handle save rule set
  const handleSaveRuleSet = () => {
    if (!ruleSetName.trim() || !ruleSetDescription.trim()) {
      alert("Please fill required fields");
      return;
    }

    console.log("Calling addRuleSet API with:", {
      name: ruleSetName,
      description: ruleSetDescription,
      standard: selectedLegalStandardIds,
    });

    // Call the API to add the rule set
    addRuleSet();
  };

  // --- Add RuleSet Dialog Handlers ---
  const toggleAddRuleSetDialog = (isOpen) => () => {
    setIsAddRuleSetDialogOpen(isOpen);
    if (!isOpen) {
      setRuleSetName("");
      setRuleSetDescription("");
      setSelectedLegalStandardIds([]);
    }
  };

  // --- Questions Dialog Handlers ---
  // Helper functions for question tree navigation
  const findQuestionServerId = (tree, questionId) => {
    if (!tree) return null;
    if (tree.id === questionId) return tree.server_id;
    const fromYes = tree.yes
      ? findQuestionServerId(tree.yes, questionId)
      : null;
    const fromNo = tree.no ? findQuestionServerId(tree.no, questionId) : null;
    return fromYes || fromNo;
  };

  const findParentQuestionId = (tree, targetId, parentId = null) => {
    if (!tree) return null;
    if (tree.id === targetId) return parentId;
    const fromYes = tree.yes
      ? findParentQuestionId(tree.yes, targetId, tree.id)
      : null;
    const fromNo = tree.no
      ? findParentQuestionId(tree.no, targetId, tree.id)
      : null;
    return fromYes || fromNo;
  };

  const findBranchType = (tree, targetId) => {
    if (!tree) return null;
    if (tree.yes && tree.yes.id === targetId) return "YES";
    if (tree.no && tree.no.id === targetId) return "NO";
    const fromYes = tree.yes ? findBranchType(tree.yes, targetId) : null;
    const fromNo = tree.no ? findBranchType(tree.no, targetId) : null;
    return fromYes || fromNo;
  };

  // Convert API tree structure to internal UI structure
  const convertApiTreeToUITree = (apiNode, uiId = 1) => {
    if (!apiNode) return null;

    // Handle question nodes
    if (apiNode.question_id) {
      return {
        id: uiId,
        text: apiNode.text,
        type: "question",
        yes: convertApiTreeToUITree(apiNode.yes_branch?.data, uiId * 2),
        no: convertApiTreeToUITree(apiNode.no_branch?.data, uiId * 2 + 1),
        server_id: parseInt(apiNode.question_id), // Store the server ID
      };
    }

    // Handle category nodes (leaf nodes)
    if (apiNode.category_id) {
      return {
        id: uiId,
        text: apiNode.description,
        type: "category",
        yes: null,
        no: null,
        server_id: parseInt(apiNode.category_id),
        requires_homologation: apiNode.requires_homologation,
      };
    }

    return null;
  };

  const handleOpenQuestions = async (ruleSet) => {
    setSelectedRuleSet(ruleSet);

    try {
      console.log("Fetching tree for rule set:", ruleSet);
      console.log(
        "Using rule_set_id:",
        ruleSet.rule_set_id,
        "(not S.No:",
        ruleSet.id,
        ")",
      );

      // First try to fetch existing questions for this rule set
      const resp = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.get_rule_set_tree,
          params: {
            rule_set_id: ruleSet.rule_set_id, // Use the actual rule set ID, not the display S.No
          },
        }),
      );

      console.log("API Response for rule set tree:", resp);

      if (!resp.error && resp.data && resp.data.tree) {
        console.log("Converting API tree to UI tree:", resp.data.tree);

        // Convert API tree structure to UI structure
        const uiTree = convertApiTreeToUITree(resp.data.tree);

        console.log("Converted UI tree:", uiTree);

        if (uiTree) {
          setQuestionTree(uiTree);
          setIsAddQuestionDialogOpen(true);
          return;
        }
      }
    } catch (error) {
      console.error("Error fetching rule set tree:", error);
    }

    console.log("Using fallback - creating new empty tree");

    // Fallback: If no existing tree found or error occurred, create a new empty tree
    setQuestionTree({
      id: 1, // Temporary UI ID, will be replaced with server ID after save
      text: "Click Edit to add your first question",
      type: "question",
      yes: null,
      no: null,
      server_id: null, // Will be set after API call
    });
    setIsAddQuestionDialogOpen(true);
  };

  const handleUpdateQuestion = (
    questionId,
    newText,
    newType,
    requiresHomologation,
  ) => {
    setQuestionTree((prev) => {
      // Create a deep copy of the tree to ensure React re-renders
      const deepClone = (node) => {
        if (!node) return null;
        return {
          ...node,
          yes: deepClone(node.yes),
          no: deepClone(node.no),
        };
      };

      const updated = deepClone(prev);

      // Update the specific question
      const updateQuestionRecursively = (node) => {
        if (node.id === questionId) {
          node.text = newText;
          node.type = newType;
          if (newType === "category") {
            node.requires_homologation = requiresHomologation;
          }
          return true;
        }
        if (node.yes && updateQuestionRecursively(node.yes)) return true;
        if (node.no && updateQuestionRecursively(node.no)) return true;
        return false;
      };

      updateQuestionRecursively(updated);
      return updated;
    });
  };

  // const handleSaveQuestion = async (
  //   questionId,
  //   questionText,
  //   questionType,
  //   ruleSetId,
  //   requiresHomologation,
  // ) => {
  //   try {
  //     let resp;

  //     // Check if this is the root question (id === 1) and hasn't been saved yet
  //     if (questionId === 1 && !questionTree.server_id) {
  //       // Use add_root_question for the first question
  //       resp = await dispatch(
  //         HandleApiActions({
  //           ...ruleEngineActions.add_root_question,
  //           params: {
  //             rule_set_id: ruleSetId || selectedRuleSet?.rule_set_id,
  //             text: questionText,
  //           },
  //           show_toast: true,
  //         }),
  //       );

  //       if (!resp.error && resp.data?.question_id) {
  //         // Update the question tree with the returned question_id
  //         setQuestionTree((prev) => {
  //           // Keep the original UI id intact; only set server_id and metadata so child references remain valid
  //           const updated = {
  //             ...prev,
  //             server_id: resp.data.question_id, // Store server ID separately
  //             lastUpdated: Date.now(), // Add timestamp to force updates
  //             version: (prev.version || 0) + 1, // Add version to force re-render
  //             _ref: Math.random(), // Force new object reference
  //           };
  //           return updated;
  //         });

  //         // Force immediate re-render
  //         setForceRender((prevForce) => prevForce + 1);
  //       }
  //     } else if (
  //       questionId !== 1 &&
  //       !findQuestionServerId(questionTree, questionId)
  //     ) {
  //       // For branch questions that haven't been saved yet, call add_branch_question
  //       const parentId = findParentQuestionId(questionTree, questionId);
  //       const branchType = findBranchType(questionTree, questionId);
  //       const parentServerId = findQuestionServerId(questionTree, parentId);

  //       console.log("Saving branch question:", {
  //         questionId,
  //         parentId,
  //         branchType,
  //         parentServerId,
  //         questionText,
  //         questionType,
  //       });

  //       if (parentServerId && branchType) {
  //         resp = await dispatch(
  //           HandleApiActions({
  //             ...ruleEngineActions.add_branch_question,
  //             params: {
  //               parent_question_id: parentServerId,
  //               branch_type: branchType.toLowerCase(), // "yes" or "no"
  //               is_category: questionType === "category",
  //               rule_set_id: ruleSetId || selectedRuleSet?.rule_set_id,
  //               text: questionText,
  //               requires_homologation:
  //                 questionType === "category"
  //                   ? requiresHomologation
  //                   : undefined,
  //             },
  //             show_toast: true,
  //           }),
  //         );

  //         // Log full response for debugging
  //         console.log("Branch add response:", resp);

  //         // Try to extract server id from common response shapes
  //         const serverId =
  //           resp?.data?.question_id ||
  //           resp?.data?.id ||
  //           resp?.data?.questionId ||
  //           resp?.question_id ||
  //           resp?.id ||
  //           null;

  //         if (!resp.error && serverId) {
  //           console.log("Branch question saved successfully:", {
  //             questionId,
  //             serverId,
  //             response: resp,
  //           });

  //           // Update the question tree in one deterministic update so nested
  //           // server_id changes are not lost by a subsequent shallow merge.
  //           setQuestionTree((prev) => {
  //             // Create completely new object references for ALL questions in the tree
  //             const createNewReferences = (node) => {
  //               if (!node) return null;
  //               return {
  //                 ...node,
  //                 _ref: Math.random(),
  //                 yes: createNewReferences(node.yes),
  //                 no: createNewReferences(node.no),
  //               };
  //             };

  //             const newQuestionTree = createNewReferences(prev);

  //             // Now update the specific question's server_id and also write
  //             // the saved text/type so the UI shows a non-placeholder state.
  //             const updateQuestionServerId = (node) => {
  //               if (!node) return false;
  //               if (node.id === questionId) {
  //                 console.log(
  //                   `Updating question ${node.id} with server_id ${serverId}`,
  //                 );
  //                 node.server_id = serverId;
  //                 node.lastUpdated = Date.now();
  //                 node.version = (node.version || 0) + 1;
  //                 // Ensure node text/type reflect the saved values (prevents placeholder text issues)
  //                 node.text = questionText;
  //                 node.type = questionType;
  //                 // Clear the isNew flag so editor does not re-open
  //                 node.isNew = false;
  //                 return true;
  //               }
  //               if (node.yes && updateQuestionServerId(node.yes)) return true;
  //               if (node.no && updateQuestionServerId(node.no)) return true;
  //               return false;
  //             };

  //             updateQuestionServerId(newQuestionTree);
  //             console.log(
  //               "Question tree after update:",
  //               JSON.stringify(newQuestionTree, null, 2),
  //             );

  //             return newQuestionTree;
  //           });

  //           // Force immediate re-render by updating the force counter
  //           setForceRender((prevForce) => prevForce + 1);
  //           // If this saved question was the one we auto-opened, clear the marker
  //           setRecentlyAddedQuestionId((curr) =>
  //             curr === questionId ? null : curr,
  //           );
  //         } else {
  //           console.error("Branch save failed or missing server ID:", resp);
  //           alert("Failed to save branch question (no server id returned)");
  //         }
  //       } else {
  //         console.error("Missing parentServerId or branchType:", {
  //           parentServerId,
  //           branchType,
  //           parentId,
  //         });
  //         alert(
  //           "Failed to save question: Parent question not found or not saved",
  //         );
  //       }
  //     } else {
  //       // Question already exists, call update API
  //       console.log("Question already saved, calling update API:", {
  //         question_id: questionId,
  //         question_text: questionText,
  //         question_type: questionType,
  //         server_id: findQuestionServerId(questionTree, questionId),
  //       });

  //       const serverId = findQuestionServerId(questionTree, questionId);
  //       const parentId = findParentQuestionId(questionTree, questionId);
  //       const parentServerId = findQuestionServerId(questionTree, parentId);
  //       const branchType = findBranchType(questionTree, questionId);

  //       if (serverId) {
  //         // Find the current question node to get its current type
  //         const findQuestionNode = (tree, targetId) => {
  //           if (!tree) return null;
  //           if (tree.id === targetId) return tree;
  //           const fromYes = tree.yes
  //             ? findQuestionNode(tree.yes, targetId)
  //             : null;
  //           const fromNo = tree.no ? findQuestionNode(tree.no, targetId) : null;
  //           return fromYes || fromNo;
  //         };

  //         const currentQuestionNode = findQuestionNode(
  //           questionTree,
  //           questionId,
  //         );
  //         const currentType = currentQuestionNode?.type || "question";
  //         const isTypeChanging = questionType !== currentType;

  //         console.log("Type conversion check:", {
  //           currentType,
  //           newType: questionType,
  //           isTypeChanging,
  //         });

  //         // Check if this is a root question (no parent)
  //         const isRootQuestion = !parentId;

  //         if (isRootQuestion) {
  //           // Use update_root_question for root question
  //           console.log("Updating root question with updateRootQuestion API");
  //           resp = await dispatch(
  //             HandleApiActions({
  //               ...ruleEngineActions.update_root_question,
  //               params: {
  //                 rule_set_id: ruleSetId || selectedRuleSet?.rule_set_id,
  //                 question_id: serverId,
  //                 text: questionText,
  //               },
  //               show_toast: true,
  //             }),
  //           );
  //         } else {
  //           // Use update_question_or_category for branch questions/categories
  //           console.log(
  //             "Updating branch question/category with update_question_or_category API",
  //           );
  //           resp = await dispatch(
  //             HandleApiActions({
  //               ...ruleEngineActions.update_question_or_category,
  //               params: {
  //                 branch_id: serverId, // The question or category ID to update
  //                 text: questionText, // Updated text
  //                 is_category: questionType === "category", // true if category, false if question
  //                 convert_type: isTypeChanging, // true if changing type (question ↔ category)
  //                 parent_question_id: parentServerId || null, // Parent question ID (null for root)
  //                 branch_type: branchType ? branchType.toLowerCase() : null, // "yes" or "no" (null for root)
  //                 requires_homologation:
  //                   questionType === "category"
  //                     ? requiresHomologation
  //                     : undefined, // Only send for categories
  //               },
  //               show_toast: true,
  //             }),
  //           );
  //         }

  //         if (!resp.error) {
  //           console.log("Update API response:", resp);

  //           // Check if the server returned a new ID (happens during type conversion)
  //           let newServerId = serverId; // Default to original ID

  //           // Handle conversion response with new_id
  //           if (resp?.data?.new_id) {
  //             newServerId = parseInt(resp.data.new_id);
  //             console.log(
  //               `Type conversion detected: old ID ${serverId} -> new ID ${newServerId}`,
  //             );
  //           } else if (resp?.data?.id) {
  //             newServerId = parseInt(resp.data.id);
  //           } else if (resp?.data?.question_id) {
  //             newServerId = parseInt(resp.data.question_id);
  //           } else if (resp?.data?.category_id) {
  //             newServerId = parseInt(resp.data.category_id);
  //           }

  //           console.log("Server ID update:", {
  //             original: serverId,
  //             new: newServerId,
  //             isTypeChanging,
  //             conversionData: resp.data,
  //           });

  //           // Update the UI tree with the new text, type, and correct server ID
  //           setQuestionTree((prev) => {
  //             const deepClone = (node) => {
  //               if (!node) return null;
  //               return {
  //                 ...node,
  //                 yes: deepClone(node.yes),
  //                 no: deepClone(node.no),
  //               };
  //             };

  //             const updated = deepClone(prev);

  //             const updateQuestionRecursively = (node) => {
  //               if (node.id === questionId) {
  //                 node.text = questionText;
  //                 node.type = questionType;
  //                 node.server_id = newServerId; // Critical: Update with the new server ID
  //                 node.lastUpdated = Date.now();
  //                 node.version = (node.version || 0) + 1;
  //                 node._ref = Math.random();
  //                 console.log(
  //                   `Updated UI node ${questionId} with new server_id: ${newServerId}`,
  //                 );
  //                 return true;
  //               }
  //               if (node.yes && updateQuestionRecursively(node.yes))
  //                 return true;
  //               if (node.no && updateQuestionRecursively(node.no)) return true;
  //               return false;
  //             };

  //             updateQuestionRecursively(updated);
  //             return updated;
  //           });

  //           // Force re-render
  //           setForceRender((prevForce) => prevForce + 1);
  //         }
  //       } else {
  //         console.error("No server ID found for question:", questionId);
  //         resp = { error: true, message: "No server ID found" };
  //       }
  //     }

  //     if (resp && resp.error) {
  //       alert("Failed to save question");
  //     }
  //   } catch (error) {
  //     console.error("Error saving question:", error);
  //     alert("Failed to save question");
  //   }
  // };

  const handleAddBranch = (parentId, branchType) => {
    // Only create UI element, no API call until user saves the question
    const newQuestion = {
      id: Date.now(),
      text: "Click Edit to add question text",
      type: "question",
      yes: null,
      no: null,
      server_id: null, // Will be set when user saves the question
      version: 0, // Initialize version for tracking updates
      isNew: true, // Mark as newly created so UI can auto-open editor
    };

    // Mark this UI id as the one to auto-open
    setRecentlyAddedQuestionId(newQuestion.id);

    setQuestionTree((prev) => {
      // Create a deep copy of the tree to ensure React re-renders
      const deepClone = (node) => {
        if (!node) return null;
        return {
          ...node,
          yes: deepClone(node.yes),
          no: deepClone(node.no),
        };
      };

      const updated = deepClone(prev);

      // Add the branch to the UI tree
      const addBranchRecursively = (node) => {
        if (node.id === parentId) {
          if (branchType === "YES" && !node.yes) node.yes = newQuestion;
          if (branchType === "NO" && !node.no) node.no = newQuestion;
        } else {
          if (node.yes) addBranchRecursively(node.yes);
          if (node.no) addBranchRecursively(node.no);
        }
      };

      addBranchRecursively(updated);
      return updated;
    });
  };

  const handleRemoveUnsavedQuestion = (questionId) => {
    console.log(`Removing unsaved question with ID: ${questionId}`);

    setQuestionTree((prev) => {
      // Create a deep copy of the tree
      const deepClone = (node) => {
        if (!node) return null;
        return {
          ...node,
          yes: deepClone(node.yes),
          no: deepClone(node.no),
        };
      };

      const updated = deepClone(prev);

      // Remove the question from the tree
      const removeQuestionRecursively = (node) => {
        if (!node) return;

        // Check if YES branch should be removed
        if (node.yes && node.yes.id === questionId) {
          node.yes = null;
          return;
        }

        // Check if NO branch should be removed
        if (node.no && node.no.id === questionId) {
          node.no = null;
          return;
        }

        // Recursively check children
        if (node.yes) removeQuestionRecursively(node.yes);
        if (node.no) removeQuestionRecursively(node.no);
      };

      removeQuestionRecursively(updated);
      return updated;
    });

    // Clear the recently added question ID if it matches
    setRecentlyAddedQuestionId((current) =>
      current === questionId ? null : current,
    );
  };

  // Handle table row click
  const handleTableRowClick = ({ params }) => {
    console.log("Selected rule set:", params.row);
    // You can add more functionality here like showing rule details
  };

  console.log(totalCount, "totalCount........");
  // --- JSX ---
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
          flexShrink: 0,
        }}
      >
        <CommonBreadcrumbs
          items={[{ label: "Legal Requirements" }, { label: "Rule Center" }]}
        />
      </Box>
      {/* Header */}
      <Box sx={{ px: 1, py: 1, display: "flex", justifyContent: "flex-end" }}>
        <CustomButtonV2
          size="small"
          variant="contained"
          btnName="Create Rule"
          handleOnClick={toggleAddRuleSetDialog(true)}
          startIcon={<AddIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
          btnStyle={{
            ml: { xs: 1, sm: 2 },
            fontSize: { xs: "0.75rem", sm: "0.9rem" },
            py: { xs: 0.5, sm: 1 },
            px: { xs: 1, sm: 2 },
            minWidth: { xs: 40, sm: 70 },
          }}
        />
      </Box>

      {/* Rule Sets Table */}
      <Box
        // elevation={2}
        sx={{
          flex: 1,
          // width: "100%",
          maxHeight: "75vh",
          m: 1,
          scrollbarColor: "#888 #f1f1f1",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CustomDataGridV2
          rows={displayRuleSets}
          columns={columns}
          isMultiLine={true}
          hideHeader={false}
          disableColumnResize={true}
          showPagination={true} // you already render pagination below
          getRowHeight={() => "auto"}
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={totalCount}
          handlePageChange={handlePageChange}
        />
        {/* <Paper
          elevation={2}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "10px",
          }}
        >
          <CustomPagination
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            hasMore={hasMore}
          />
        </Paper> */}
      </Box>

      {/* Add Rule Set Dialog */}
      {/* <CustomAddDialog
        open={isAddRuleSetDialogOpen}
        onClose={toggleAddRuleSetDialog(false)}
        dialogTitle="Add Rule Set"
        onConfirm={handleSaveRuleSet}
        onDisabled={!ruleSetName.trim() || !ruleSetDescription.trim()}
        dialogContent={
          <Box sx={{ width: 400, maxWidth: 450 }}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <CustomTextFieldN
                  size="small"
                  label="Rule Set Name *"
                  placeholder="Enter rule set name"
                  variant="standard"
                  value={ruleSetName}
                  setValue={setRuleSetName}
                  txtFldStyle={{ width: "100%" }}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomTextFieldN
                  size="small"
                  label="Description *"
                  placeholder="Enter description"
                  variant="standard"
                  value={ruleSetDescription}
                  setValue={setRuleSetDescription}
                  txtFldStyle={{ width: "100%" }}
                />
              </Grid>

              <Grid item xs={12}>
                <MultiSelectTextFieldNVs
                  size="small"
                  label="Select Legal Standards"
                  mutiple
                  optionsList={legalStandardsOptionsList}
                  selectedValues={selectedLegalStandardIds}
                  setSelectedValues={setSelectedLegalStandardIds}
                  sltFldStyle={{ width: "100%", minWidth: "100%" }}
                />
              </Grid>
            </Grid>
          </Box>
        }
      /> */}

      <CustomDialogBoxV2
        title={
          <Box
            sx={{
              fontSize: "16px",
              fontWeight: "bold",
              color: "#000000ff",
            }}
          >
            Add Rule
          </Box>
        }
        open={isAddRuleSetDialogOpen}
        onClose={toggleAddRuleSetDialog(false)}
        successButtonName="Save & Continue"
        rejectButtonName="Cancel"
        onDisabled={!ruleSetName.trim() || !ruleSetDescription.trim()}
        actionButtonStyle={"continuous"}
        onClickSuccess={handleSaveRuleSet}
        onClickReject={toggleAddRuleSetDialog(false)}
      >
        <Box sx={{ width: 400, maxWidth: 450 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <CustomFormTextFieldV2
                size="small"
                label="Rule Set Name"
                required
                placeholder="Enter rule set name"
                type="text"
                fldType="text1"
                value={ruleSetName}
                setValue={setRuleSetName}
                txtFldStyle={{ width: "100%" }}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomFormTextFieldV2
                size="small"
                label="Description"
                required
                placeholder="Enter description"
                type="text"
                fldType="text1" // 👈 minimum 1 character
                value={ruleSetDescription}
                setValue={setRuleSetDescription}
                txtFldStyle={{ width: "100%" }}
                isMultiline={true}
                // minRows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomCheckboxMultiSelect
                size="small"
                label="Select Legal Standards"
                required={false}
                selectedValues={selectedLegalStandardIds}
                setSelectedValues={setSelectedLegalStandardIds}
                optionsList={
                  legalStandardsOptionsList?.map((item) => ({
                    label: item.label ?? item.name, // adjust if needed
                    value: item.value ?? item.id,
                  })) || []
                }
                txtFldStyle={{ width: "100%", minWidth: "100%" }}
                tag="other"
              />
            </Grid>
          </Grid>
        </Box>
      </CustomDialogBoxV2>
      {/* Add Questions Dialog */}

      {/* <CustomDialogBoxV2
        title={
          <Box
            sx={{
              fontSize: "16px",
              fontWeight: "bold",
              color: "#000000ff",
            }}
          >
            {`Add Questions to Rule: ${selectedRuleSet?.ruleSetName}`}
          </Box>
        }
        open={isAddQuestionDialogOpen}
        paperSx={{ maxWidth: "100vw", height: "100vh" }}
        onClose={() => setIsAddQuestionDialogOpen(false)}
        onDisabled={!ruleSetName.trim() || !ruleSetDescription.trim()}
        onClickSuccess={handleSaveRuleSet}
        onClickReject={toggleAddRuleSetDialog(false)}
        fullScreen
      >
        <Box sx={{ width: "100vw", height: "100vh" }}>
     
          <Box
            sx={{ mb: 2, p: 2, backgroundColor: "#eaebebff", borderRadius: 1 }}
          >
            <Typography variant="body2" sx={{ color: "black" }}>
              <strong>How to create questions:</strong>
              <br />
              1. First, click "Edit" on the root question and save it
              <br />
              2. Only after saving a question, you can add YES/NO branches to it
              <br />
              3. Each new branch question must be edited and saved individually
              before adding more branches
              <br />
              4. Questions show "✓ Saved" when ready for branches, "⚠ Save
              required" when not
            </Typography>
          </Box>

          {questionTree && (
            <QuestionNode
              key={`question-tree-${questionTree.id || "root"}-${
                questionTree.server_id || "unsaved"
              }-${questionTree.version || 0}-${questionTree.lastUpdated || 0}-${
                questionTree._ref || 0
              }-${forceRender}`}
              question={questionTree}
              onAddBranch={handleAddBranch}
              onUpdateQuestion={handleUpdateQuestion}
              onSaveQuestion={handleSaveQuestion}
              onRemoveUnsavedQuestion={handleRemoveUnsavedQuestion}
              ruleSetId={selectedRuleSet?.rule_set_id}
              forceRender={forceRender}
              autoOpenId={recentlyAddedQuestionId}
              clearAutoOpenId={() => setRecentlyAddedQuestionId(null)}
              isRoot={true} // Mark this as the root question
            />
          )}
        </Box>
      </CustomDialogBoxV2> */}
    </Box>
  );
}

export default RuleCenter;

const styles = {
  fontSub1: { fontSize: fontSizeDetails.subtitle2.fontSize },
  fontBody: { fontSize: fontSizeDetails.body1.fontSize },
};
const treeStyles = {
  node: {
    position: "relative",
    border: "1px solid #ddd",
    p: 2,
    borderRadius: 2,
    mt: 2,
    backgroundColor: "#fff",
  },

  childrenWrapper: {
    display: "flex",
    justifyContent: "center",
    gap: 6,
    mt: 3,
    position: "relative",
  },

  branch: {
    position: "relative",
    minWidth: 320,
  },

  verticalLine: {
    position: "absolute",
    top: -24,
    left: "50%",
    width: "2px",
    height: "24px",
    backgroundColor: "#ccc",
    transform: "translateX(-50%)",
  },

  horizontalLine: {
    position: "absolute",
    top: -24,
    left: 0,
    right: 0,
    height: "2px",
    backgroundColor: "#ccc",
  },

  branchLabel: {
    textAlign: "center",
    fontWeight: "bold",
    fontSize: 12,
    mb: 1,
  },
};
